import type {
  DetectTimezoneResult,
  Environment,
} from "@multisystemsuite/timezone-engine-shared-types";
import { isBrowser, isNode, safeIntlSupported } from "@multisystemsuite/timezone-engine-utils";
import { isValidIanaTimezone, normalizeTimezone } from "./normalization.js";

const DEFAULT_TIMEZONE = "UTC";

function detectViaIntl(): string | undefined {
  if (!safeIntlSupported()) return undefined;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function detectViaEnv(): string | undefined {
  if (isNode()) {
    return process.env["TZ"] ?? process.env["DEFAULT_TIMEZONE"];
  }
  return undefined;
}

function getEnvironment(): Environment {
  if (isBrowser()) return "browser";
  if (isNode()) return "node";
  return "ssr";
}

/** Detect timezone in browser environment */
export function detectBrowserTimezone(): DetectTimezoneResult {
  const timezone = detectViaIntl() ?? DEFAULT_TIMEZONE;
  return { timezone, source: "intl", environment: "browser" };
}

/** Detect timezone in Node.js environment */
export function detectNodeTimezone(): DetectTimezoneResult {
  const envTz = detectViaEnv();
  const timezone = envTz ?? detectViaIntl() ?? DEFAULT_TIMEZONE;
  return {
    timezone,
    source: envTz ? "env" : "intl",
    environment: "node",
  };
}

/** Detect server timezone */
export function detectServerTimezone(defaultTimezone = DEFAULT_TIMEZONE): DetectTimezoneResult {
  const envTz = detectViaEnv();
  const timezone = envTz ?? defaultTimezone;
  return {
    timezone,
    source: envTz ? "env" : "config",
    environment: isNode() ? "node" : "ssr",
  };
}

/** Universal timezone detection — SSR-safe */
export function detectTimezone(fallback = DEFAULT_TIMEZONE): DetectTimezoneResult {
  const intlTz = detectViaIntl();
  const envTz = detectViaEnv();

  if (intlTz) {
    return { timezone: intlTz, source: "intl", environment: getEnvironment() };
  }
  if (envTz) {
    return { timezone: envTz, source: "env", environment: getEnvironment() };
  }
  return { timezone: fallback, source: "config", environment: getEnvironment() };
}

/** Get user's current timezone */
export function getUserTimezone(fallback = DEFAULT_TIMEZONE): string {
  return detectTimezone(fallback).timezone;
}

/** Resolve timezone from header, cookie, or query param value */
export function resolveTimezoneFromValue(
  value: string | undefined,
  fallback = DEFAULT_TIMEZONE,
): DetectTimezoneResult {
  const normalized = normalizeTimezone(value);
  if (value && isValidTimezone(normalized)) {
    return { timezone: normalized, source: "header", environment: getEnvironment() };
  }
  return detectTimezone(fallback);
}

/**
 * Validate that a timezone string (after normalisation) is a valid IANA id
 * recognised by the runtime Intl implementation.
 */
export function isValidTimezone(timeZone: string | null | undefined): boolean {
  const tz = normalizeTimezone(timeZone);
  return isValidIanaTimezone(tz);
}

/** List all supported IANA timezones via Intl */
export function listSupportedTimezones(): string[] {
  if (typeof Intl.supportedValuesOf === "function") {
    return Intl.supportedValuesOf("timeZone");
  }
  return [];
}
